import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { apiClient } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Plus, Search, FileText, TrendingUp, CheckCircle2, FileEdit, Eye, Trash2, Copy, GitBranch, Upload } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger
} from "@/components/ui/alert-dialog";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";

const statusClass = (s) => `status-${s}`;

const Kpi = ({ label, value, icon: Icon, accent, testId }) => (
  <Card className="rounded-sm border-[#E5E7EB] p-5 shadow-none" data-testid={testId}>
    <div className="flex items-start justify-between">
      <div>
        <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">{label}</div>
        <div className="font-heading mt-2 text-3xl font-bold ep-text-primary tabular-nums">{value}</div>
      </div>
      <div className={`flex h-10 w-10 items-center justify-center rounded-sm ${accent}`}>
        <Icon className="h-5 w-5 text-white" strokeWidth={1.75} />
      </div>
    </div>
  </Card>
);

const DashboardPage = () => {
  const { user } = useAuth();
  const nav = useNavigate();
  const [quotes, setQuotes] = useState([]);
  const [stats, setStats] = useState({ total: 0, by_status: {}, total_value: 0 });
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const [qs, st] = await Promise.all([
        apiClient.get("/quotes"),
        apiClient.get("/quotes/stats"),
      ]);
      setQuotes(qs.data);
      setStats(st.data);
    } catch (e) {
      toast.error("Failed to load quotes");
    }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const totalValue = (quote) =>
    quote.line_items.reduce((s, li) => s + Number(li.quantity || 0) * Number(li.unit_price || 0), 0);

  const filtered = useMemo(() => {
    return quotes.filter((qt) => {
      if (statusFilter !== "all" && qt.status !== statusFilter) return false;
      if (!q.trim()) return true;
      const t = q.toLowerCase();
      return (
        qt.quote_ref?.toLowerCase().includes(t) ||
        qt.customer_name?.toLowerCase().includes(t) ||
        qt.subject?.toLowerCase().includes(t) ||
        qt.owner_name?.toLowerCase().includes(t)
      );
    });
  }, [quotes, q, statusFilter]);

  const handleDelete = async (id) => {
    try {
      await apiClient.delete(`/quotes/${id}`);
      toast.success("Quote deleted");
      load();
    } catch {
      toast.error("Delete failed");
    }
  };

  const handleDuplicate = async (id) => {
    try {
      const { data } = await apiClient.post(`/quotes/${id}/duplicate`);
      toast.success(`Duplicated as ${data.quote_ref}`);
      nav(`/quotes/${data.id}/edit`);
    } catch {
      toast.error("Duplicate failed");
    }
  };

  const handleRevise = async (id) => {
    try {
      const { data } = await apiClient.post(`/quotes/${id}/revise`);
      toast.success(`Revised as ${data.quote_ref}`);
      nav(`/quotes/${data.id}/edit`);
    } catch {
      toast.error("Revise failed");
    }
  };

  // -------- Import PDF --------
  const [importOpen, setImportOpen] = useState(false);
  const [importSaving, setImportSaving] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importEditingId, setImportEditingId] = useState(null); // null = create; otherwise editing existing imported quote
  const [importForm, setImportForm] = useState({
    quote_ref: "",
    quote_date: new Date().toISOString().slice(0, 10),
    customer_name: "",
    subject: "",
    status: "sent",
  });
  const openImport = () => {
    // Manual entry — reference, customer, and date all typed by user from the PDF.
    setImportFile(null);
    setImportEditingId(null);
    setImportForm({
      quote_ref: "",
      quote_date: new Date().toISOString().slice(0, 10),
      customer_name: "",
      subject: "",
      status: "sent",
    });
    setImportOpen(true);
  };
  const openImportEdit = (qt) => {
    setImportFile(null);
    setImportEditingId(qt.id);
    setImportForm({
      quote_ref: qt.quote_ref || "",
      quote_date: qt.quote_date || new Date().toISOString().slice(0, 10),
      customer_name: qt.customer_name || "",
      subject: qt.subject || "",
      status: qt.status || "sent",
    });
    setImportOpen(true);
  };
  const submitImport = async () => {
    if (!importForm.quote_ref.trim()) return toast.error("Reference is required");
    if (!importForm.customer_name.trim()) return toast.error("Customer name is required");
    if (!importEditingId && !importFile) return toast.error("Please choose a PDF");
    setImportSaving(true);
    try {
      const fd = new FormData();
      fd.append("quote_ref", importForm.quote_ref);
      fd.append("quote_date", importForm.quote_date);
      fd.append("customer_name", importForm.customer_name);
      fd.append("subject", importForm.subject);
      fd.append("status", importForm.status);
      if (importFile) fd.append("file", importFile);
      if (importEditingId) {
        const { data } = await apiClient.patch(`/quotes/${importEditingId}/import-meta`, fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        toast.success(`Updated ${data.quote_ref}`);
      } else {
        const { data } = await apiClient.post("/quotes/import", fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        toast.success(`Imported as ${data.quote_ref}`);
      }
      setImportOpen(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    }
    setImportSaving(false);
  };

  const viewQuote = async (qt) => {
    if (qt.is_imported) {
      try {
        const { data } = await apiClient.get(`/quotes/${qt.id}/pdf`);
        // Open PDF in a new tab
        const w = window.open("", "_blank");
        if (w) {
          w.document.write(
            `<html><head><title>${data.filename}</title></head><body style="margin:0"><embed src="${data.pdf}" type="application/pdf" width="100%" height="100%" /></body></html>`
          );
        }
      } catch {
        toast.error("Failed to load PDF");
      }
    } else {
      nav(`/quotes/${qt.id}`);
    }
  };

  return (
    <div className="space-y-6" data-testid="dashboard-page">
      <div className="flex items-end justify-between">
        <div>
          <h1 className="font-heading text-3xl font-bold ep-text-primary">Dashboard</h1>
          <p className="mt-1 text-sm text-slate-500">
            {user?.role === "admin" ? "All company quotations" : "Your quotations"}
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            className="rounded-sm"
            onClick={openImport}
            data-testid="import-pdf-button"
          >
            <Upload className="mr-2 h-4 w-4" /> Import PDF
          </Button>
          <Button
            className="rounded-sm ep-amber hover:ep-amber"
            onClick={() => nav("/quotes/new")}
            data-testid="new-quote-button"
          >
            <Plus className="mr-2 h-4 w-4" /> New Quote
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Kpi label="Total Quotes" value={stats.total} icon={FileText} accent="bg-[#0F2537]" testId="kpi-total" />
        <Kpi label="Total Value (AED)" value={stats.total_value.toLocaleString(undefined, { maximumFractionDigits: 0 })} icon={TrendingUp} accent="bg-[#FF6B00]" testId="kpi-value" />
        <Kpi label="Sent" value={stats.by_status?.sent || 0} icon={FileEdit} accent="bg-blue-600" testId="kpi-sent" />
        <Kpi label="Accepted" value={stats.by_status?.accepted || 0} icon={CheckCircle2} accent="bg-emerald-600" testId="kpi-accepted" />
      </div>

      <Card className="rounded-sm border-[#E5E7EB] shadow-none">
        <div className="flex flex-col gap-3 border-b border-[#E5E7EB] p-4 md:flex-row md:items-center md:justify-between">
          <div className="relative w-full md:w-80">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search by ref, customer, subject…"
              className="rounded-sm pl-9"
              data-testid="search-input"
            />
          </div>
          <div className="flex flex-wrap gap-2">
            {["all", "draft", "sent", "accepted", "rejected"].map((s) => (
              <button
                key={s}
                onClick={() => setStatusFilter(s)}
                data-testid={`filter-${s}`}
                className={`rounded-sm border px-3 py-1.5 text-xs uppercase tracking-wider transition-colors ${
                  statusFilter === s
                    ? "border-[#0F2537] bg-[#0F2537] text-white"
                    : "border-[#E5E7EB] bg-white text-slate-600 hover:bg-slate-50"
                }`}
              >
                {s}
              </button>
            ))}
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="quotes-table">
            <thead>
              <tr className="border-b border-[#E5E7EB] bg-slate-50 text-left text-xs uppercase tracking-wider text-slate-500">
                <th className="px-4 py-3 font-semibold">Quote Ref</th>
                <th className="px-4 py-3 font-semibold">Date</th>
                <th className="px-4 py-3 font-semibold">Customer</th>
                <th className="px-4 py-3 font-semibold">Subject</th>
                {user?.role === "admin" && <th className="px-4 py-3 font-semibold">Sales Person</th>}
                <th className="px-4 py-3 text-right font-semibold">Value (AED)</th>
                <th className="px-4 py-3 font-semibold">Status</th>
                <th className="px-4 py-3 text-right font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={8} className="px-4 py-10 text-center text-slate-400">Loading…</td></tr>
              ) : filtered.length === 0 ? (
                <tr><td colSpan={8} className="px-4 py-10 text-center text-slate-400" data-testid="empty-state">
                  No quotes yet. Click &quot;New Quote&quot; to create your first one.
                </td></tr>
              ) : filtered.map((qt) => (
                <tr key={qt.id} className="border-b border-[#E5E7EB] hover:bg-slate-50" data-testid={`quote-row-${qt.quote_ref}`}>
                  <td className="px-4 py-3 font-medium ep-text-primary">{qt.quote_ref}</td>
                  <td className="px-4 py-3 text-slate-600">{qt.quote_date}</td>
                  <td className="px-4 py-3">
                    {qt.customer_name}
                    {qt.is_imported && (
                      <span className="ml-2 rounded-sm bg-slate-200 px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wider text-slate-600">
                        Imported PDF
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-slate-600">{qt.subject?.slice(0, 50)}</td>
                  {user?.role === "admin" && <td className="px-4 py-3 text-slate-600">{qt.owner_name}</td>}
                  <td className="px-4 py-3 text-right tabular-nums">{totalValue(qt).toLocaleString(undefined, { maximumFractionDigits: 2 })}</td>
                  <td className="px-4 py-3">
                    <Badge className={`rounded-sm border-0 px-2 py-0.5 text-[10px] uppercase ${statusClass(qt.status)}`}>
                      {qt.status}
                    </Badge>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex justify-end gap-1">
                      <Button variant="ghost" size="icon" className="h-8 w-8 rounded-sm" onClick={() => viewQuote(qt)} data-testid={`view-${qt.id}`} title={qt.is_imported ? "Open imported PDF" : "View quote"}>
                        <Eye className="h-4 w-4" />
                      </Button>
                      {qt.is_imported ? (
                        <Button variant="ghost" size="icon" className="h-8 w-8 rounded-sm" onClick={() => openImportEdit(qt)} data-testid={`edit-${qt.id}`} title="Edit imported quote">
                          <FileEdit className="h-4 w-4" />
                        </Button>
                      ) : (
                        <Button asChild variant="ghost" size="icon" className="h-8 w-8 rounded-sm" data-testid={`edit-${qt.id}`}>
                          <Link to={`/quotes/${qt.id}/edit`}><FileEdit className="h-4 w-4" /></Link>
                        </Button>
                      )}
                      {!qt.is_imported && (
                        <Button variant="ghost" size="icon" className="h-8 w-8 rounded-sm" onClick={() => handleDuplicate(qt.id)} data-testid={`duplicate-${qt.id}`} title="Duplicate quote">
                          <Copy className="h-4 w-4" />
                        </Button>
                      )}
                      {!qt.is_imported && (
                        <Button variant="ghost" size="icon" className="h-8 w-8 rounded-sm" onClick={() => handleRevise(qt.id)} data-testid={`revise-${qt.id}`} title="Revise quote (R1, R2…)">
                          <GitBranch className="h-4 w-4" />
                        </Button>
                      )}
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button variant="ghost" size="icon" className="h-8 w-8 rounded-sm text-red-600 hover:text-red-700" data-testid={`delete-${qt.id}`}>
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent className="rounded-sm">
                          <AlertDialogHeader>
                            <AlertDialogTitle>Delete this quote?</AlertDialogTitle>
                            <AlertDialogDescription>
                              {qt.quote_ref} — {qt.customer_name}. This action cannot be undone.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel className="rounded-sm">Cancel</AlertDialogCancel>
                            <AlertDialogAction onClick={() => handleDelete(qt.id)} className="rounded-sm bg-red-600 hover:bg-red-700" data-testid={`confirm-delete-${qt.id}`}>
                              Delete
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Import PDF Dialog */}
      <Dialog open={importOpen} onOpenChange={setImportOpen}>
        <DialogContent className="rounded-sm">
          <DialogHeader>
            <DialogTitle>{importEditingId ? "Edit Imported Quote" : "Import Existing Quote (PDF)"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
              <div>
                <Label className="text-xs font-semibold uppercase tracking-wider text-slate-600">Quote Reference *</Label>
                <Input value={importForm.quote_ref} onChange={(e) => setImportForm((f) => ({ ...f, quote_ref: e.target.value }))} placeholder="Type ref exactly as shown on PDF" className="mt-1 rounded-sm" data-testid="import-ref-input" />
              </div>
              <div>
                <Label className="text-xs font-semibold uppercase tracking-wider text-slate-600">Quote Date *</Label>
                <Input type="date" value={importForm.quote_date} onChange={(e) => setImportForm((f) => ({ ...f, quote_date: e.target.value }))} className="mt-1 rounded-sm" data-testid="import-date-input" />
              </div>
              <div className="md:col-span-2">
                <Label className="text-xs font-semibold uppercase tracking-wider text-slate-600">Customer Name *</Label>
                <Input value={importForm.customer_name} onChange={(e) => setImportForm((f) => ({ ...f, customer_name: e.target.value }))} placeholder="Same as on PDF" className="mt-1 rounded-sm" data-testid="import-customer-input" />
              </div>
              <div className="md:col-span-2">
                <Label className="text-xs font-semibold uppercase tracking-wider text-slate-600">Subject</Label>
                <Input value={importForm.subject} onChange={(e) => setImportForm((f) => ({ ...f, subject: e.target.value }))} placeholder="e.g. Plastic Pallet Order" className="mt-1 rounded-sm" data-testid="import-subject-input" />
              </div>
              <div>
                <Label className="text-xs font-semibold uppercase tracking-wider text-slate-600">Status</Label>
                <select
                  value={importForm.status}
                  onChange={(e) => setImportForm((f) => ({ ...f, status: e.target.value }))}
                  className="mt-1 w-full rounded-sm border border-[#E5E7EB] bg-white px-2 py-2 text-sm"
                  data-testid="import-status-select"
                >
                  <option value="draft">Draft</option>
                  <option value="sent">Sent</option>
                  <option value="accepted">Accepted</option>
                  <option value="rejected">Rejected</option>
                </select>
              </div>
            </div>
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider text-slate-600">
                Quote PDF {importEditingId ? "(leave empty to keep current)" : "*"}
              </Label>
              <div className="mt-1 rounded-sm border border-dashed border-[#E5E7EB] bg-slate-50 p-3">
                <input
                  type="file"
                  accept="application/pdf"
                  onChange={(e) => setImportFile(e.target.files?.[0] || null)}
                  className="w-full text-sm"
                  data-testid="import-file-input"
                />
                {importFile && (
                  <div className="mt-1 text-xs text-slate-600">
                    Selected: {importFile.name} ({(importFile.size / 1024).toFixed(0)} KB)
                  </div>
                )}
                <div className="mt-1 text-[10px] text-slate-400">Max 10MB. {importEditingId ? "Uploading a new PDF will replace the existing one." : "The PDF will be attached to this quote entry."}</div>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setImportOpen(false)} className="rounded-sm">Cancel</Button>
            <Button onClick={submitImport} disabled={importSaving} className="rounded-sm ep-amber hover:ep-amber" data-testid="save-import-button">
              {importSaving ? "Saving…" : importEditingId ? "Save Changes" : "Import Quote"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DashboardPage;
